import type { CurvaDocument } from "@/document/model/document";
import { getAllSceneNodes, getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";

export interface ObjectSnapGuide {
  orientation: "vertical" | "horizontal";
  position: number;
  from: number;
  to: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

export interface ObjectSnapCandidate {
  nodeId: string;
  bounds: Rect;
}

interface AxisLine {
  value: number;
  start: number;
  end: number;
}

interface AxisSnap {
  delta: number;
  distance: number;
  guide: ObjectSnapGuide;
}

export function visibleObjectSnapCandidates(
  document: CurvaDocument,
  movingNodeIds: string[],
  viewport: Rect,
): ObjectSnapCandidate[] {
  const excluded = movingNodeAndDescendantIds(document, movingNodeIds);
  return getAllSceneNodes(document)
    .filter(node => node.visible && !excluded.has(node.id) && rectsIntersect(node.bounds, viewport))
    .map(node => ({ nodeId: node.id, bounds: node.bounds }));
}

export function snapBoundsToObjects(
  movingBounds: Rect,
  candidates: ObjectSnapCandidate[],
  zoom: number,
): { dx: number; dy: number; guides: ObjectSnapGuide[] } {
  const tolerance = 5 / Math.max(0.0001, zoom);
  const xSnap = closestAxisSnap(
    boundsXLines(movingBounds),
    candidates.flatMap(candidate => boundsXLines(candidate.bounds)),
    tolerance,
    "vertical",
  );
  const ySnap = closestAxisSnap(
    boundsYLines(movingBounds),
    candidates.flatMap(candidate => boundsYLines(candidate.bounds)),
    tolerance,
    "horizontal",
  );

  return {
    dx: xSnap?.delta ?? 0,
    dy: ySnap?.delta ?? 0,
    guides: [xSnap?.guide, ySnap?.guide].filter((guide): guide is ObjectSnapGuide => Boolean(guide)),
  };
}

function movingNodeAndDescendantIds(document: CurvaDocument, movingNodeIds: string[]): Set<string> {
  const movingIds = new Set(movingNodeIds);
  const excluded = new Set<string>();
  for (const node of getAllSceneNodes(document)) {
    if (movingIds.has(node.id)) {
      addNodeAndDescendants(node, excluded);
    }
  }
  return excluded;
}

function addNodeAndDescendants(node: SceneNode, ids: Set<string>): void {
  ids.add(node.id);
  for (const child of getSceneNodeChildren(node) ?? []) {
    addNodeAndDescendants(child, ids);
  }
}

function closestAxisSnap(
  movingLines: AxisLine[],
  targetLines: AxisLine[],
  tolerance: number,
  orientation: ObjectSnapGuide["orientation"],
): AxisSnap | undefined {
  let best: AxisSnap | undefined;
  for (const moving of movingLines) {
    for (const target of targetLines) {
      const delta = target.value - moving.value;
      const distance = Math.abs(delta);
      if (distance > tolerance || (best && distance >= best.distance)) {
        continue;
      }

      best = {
        delta,
        distance,
        guide: {
          orientation,
          position: target.value,
          from: Math.min(moving.start, moving.end, target.start, target.end),
          to: Math.max(moving.start, moving.end, target.start, target.end),
        },
      };
    }
  }
  return best;
}

function boundsXLines(bounds: Rect): AxisLine[] {
  const x1 = bounds.x;
  const x2 = bounds.x + bounds.width;
  const cx = bounds.x + bounds.width / 2;
  return [
    { value: x1, start: bounds.y, end: bounds.y + bounds.height },
    { value: cx, start: bounds.y, end: bounds.y + bounds.height },
    { value: x2, start: bounds.y, end: bounds.y + bounds.height },
  ];
}

function boundsYLines(bounds: Rect): AxisLine[] {
  const y1 = bounds.y;
  const y2 = bounds.y + bounds.height;
  const cy = bounds.y + bounds.height / 2;
  return [
    { value: y1, start: bounds.x, end: bounds.x + bounds.width },
    { value: cy, start: bounds.x, end: bounds.x + bounds.width },
    { value: y2, start: bounds.x, end: bounds.x + bounds.width },
  ];
}

function rectsIntersect(left: Rect, right: Rect): boolean {
  return left.x <= right.x + right.width &&
    left.x + left.width >= right.x &&
    left.y <= right.y + right.height &&
    left.y + left.height >= right.y;
}
